import { useState, useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { filter_caret_down, filter_caret_up, info_outline, Icon } from '../../../Icon/Icon';
import './FilterSection.css';
import Checkbox from '../../../Form/Checkbox/Checkbox';
import Slider from '../../../Slider/Slider';
import { ApiFacetLoop54 } from '../../../../types';
import { toAlphaNumeric } from '../../../utils';
import { FilterContainer } from '../../../../state/filters';
import { useTranslation } from '../../../../i18n/i18n';
import { useParams } from 'react-router-dom';
import { useConfig } from '../../../../config';

type FilterSectionProps = {
	filter: ApiFacetLoop54;
	onFilterChange: (filterName: string, itemValue: string | [number, number], isApplied: boolean) => void;
	description?: string | null;
};

const getDecimalPlaces = (value: number) => {
	const valueString = value.toString();
	if (valueString.includes('e-')) {
		const exponent = Number(valueString.split('e-')[1]);
		return Number.isFinite(exponent) ? exponent : 0;
	}
	const dotIndex = valueString.indexOf('.');
	return dotIndex === -1 ? 0 : valueString.length - dotIndex - 1;
};

const getStepFromRange = (min: number, max: number) => {
	const decimals = Math.max(getDecimalPlaces(min), getDecimalPlaces(max));
	return decimals > 0 ? 1 / Math.pow(10, decimals) : 1;
};

export const FilterSection = ({ filter, onFilterChange, description }: FilterSectionProps) => {
	const [isFullyExpanded, setIsFullyExpanded] = useState(false);
	const { name, type } = filter;
	const INITIAL_NUM_OF_ITEMS_TO_DISPLAY = 4;
	const { getAttributeNameFromAttributeString, getUnitFromAttributeString } = FilterContainer.useContainer();
	const t = useTranslation().filtering;
	const { perma } = useParams<{ perma: string }>();
	const permaRef = useRef<string>(perma);
	const { locale, currency, currencyBeforeAmount } = useConfig();

	useEffect(() => {
		if (perma !== permaRef.current) {
			permaRef.current = perma;
			setIsFullyExpanded(false);
		}
	}, [perma]);

	const [displayedItems, setDisplayedItems] = useState(
		filter.type === 'distinct' && filter.items ? filter.items.slice(0, INITIAL_NUM_OF_ITEMS_TO_DISPLAY) : []
	);

	const handleSliderChange = (min: number, max: number) => {
		onFilterChange(name, [min, max], true);
	};

	useEffect(() => {
		if (type === 'distinct' && filter.items) {
			let sortedItems = [...filter.items];

			if (!isFullyExpanded) {
				sortedItems.sort((a, b) => b.count - a.count);
			} else {
				sortedItems.sort((a, b) => a.item.localeCompare(b.item, locale, { sensitivity: 'base' }));
			}

			const itemsToDisplay = isFullyExpanded
				? sortedItems
				: sortedItems.slice(0, INITIAL_NUM_OF_ITEMS_TO_DISPLAY);
			setDisplayedItems(itemsToDisplay);
		}
	}, [isFullyExpanded, filter, name]);

	const handleFilterChecked = (itemValue: string, isApplied: boolean) => {
		onFilterChange(name, itemValue, isApplied);
	};

	if (type === 'distinct' && (!filter.items || filter.items.length < 1)) return null;
	if (type === 'range') {
		if (filter.min == null || filter.max == null) return null;
		// hide range filters that have no usable span
		const step = getStepFromRange(filter.min, filter.max);
		if (filter.min === filter.max || filter.max - filter.min < step) return null;
	}

	const rangeStep =
		type === 'range' && filter.min != null && filter.max != null ? getStepFromRange(filter.min, filter.max) : 1;

	return type === 'range' ? (
		<ExpandableSection
			heading={name === 'price' ? t.price : getAttributeNameFromAttributeString(name)}
			className="slider-wrapper"
			description={description}
		>
			<Slider
				minValue={filter.min}
				maxValue={filter.max}
				selectedMinValue={filter.selectedMin}
				selectedMaxValue={filter.selectedMax}
				onSliderChange={handleSliderChange}
				step={rangeStep}
				minDistance={rangeStep}
				unit={name === 'price' ? currency : getUnitFromAttributeString(name)!}
				unitBeforeValue={name === 'price' && currencyBeforeAmount}
			/>
		</ExpandableSection>
	) : (
		<ExpandableSection
			heading={
				name === 'brand'
					? t.brand
					: name === 'bottomCategory'
						? t.category
						: getAttributeNameFromAttributeString(name)
			}
			description={description}
		>
			<>
				<ul className="filter-items-list">
					{displayedItems.map(
						(item, i) =>
							item.count > 0 && (
								<li key={item.item + i}>
									<div className="filter-item">
										<Checkbox
											id={toAlphaNumeric(String(item.item))}
											label={String(item.item)}
											checked={item.selected}
											onChange={() => handleFilterChecked(item.item, !item.selected)}
										/>
										{getUnitFromAttributeString(name) && (
											<span className="filter-item-unit">{getUnitFromAttributeString(name)}</span>
										)}
										<span className="filter-item-count">({item.count})</span>
									</div>
								</li>
							)
					)}
				</ul>
				{filter.items && filter.items.length > INITIAL_NUM_OF_ITEMS_TO_DISPLAY && (
					<p className="show-more" onClick={() => setIsFullyExpanded((prev) => !prev)}>
						{!isFullyExpanded ? <>{t.more2}</> : <>{t.less}</>}
					</p>
				)}
			</>
		</ExpandableSection>
	);
};

export const ExpandableSection = ({
	heading,
	initiallyExpanded,
	className,
	description,
	children,
}: {
	heading: string;
	initiallyExpanded?: boolean;
	className?: string;
	description?: string | null;
	children: JSX.Element;
}) => {
	const [isOpen, setIsOpen] = useState(initiallyExpanded ?? true);
	const [tooltipVisible, setTooltipVisible] = useState(false);
	const iconRef = useRef<HTMLSpanElement>(null);
	const tooltipId = useId();

	const getTooltipStyle = () => {
		if (!iconRef.current) return {};
		const rect = iconRef.current.getBoundingClientRect();
		return {
			position: 'fixed' as const,
			left: Math.min(rect.left, window.innerWidth - 292),
			top: rect.bottom + 6,
		};
	};

	return (
		<>
			<div className="filter-items-top-bar">
				<div className="column">
					<p className="heading">
						{heading}
						{description && (
							<span
								className="filter-description-tooltip-wrapper"
								tabIndex={0}
								ref={iconRef}
								aria-describedby={tooltipVisible ? tooltipId : undefined}
								aria-label="Description"
								onMouseEnter={() => setTooltipVisible(true)}
								onMouseLeave={() => setTooltipVisible(false)}
								onFocus={() => setTooltipVisible(true)}
								onBlur={() => setTooltipVisible(false)}
							>
								<Icon shape={info_outline} size={16} className="filter-description-icon" />
							</span>
						)}
					</p>
				</div>
				<div className="column">
					<span className="expand-caret" onClick={() => setIsOpen(!isOpen)}>
						<Icon shape={isOpen ? filter_caret_down : filter_caret_up} />
					</span>
				</div>
			</div>
			{tooltipVisible &&
				description &&
				createPortal(
					<div className="filter-description-tooltip" role="tooltip" id={tooltipId} style={getTooltipStyle()}>
						{description}
					</div>,
					document.body
				)}
			<div className={`filter-items-container ${isOpen ? 'open' : ''} ${className ? className : ''}`}>
				{children}
			</div>
		</>
	);
};
